import { Tag, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { currencySymbol } from '@/lib/currency';
import type { Currency, PricingPlanRequest } from '@/types/offering';

// ─── Currency config ──────────────────────────────────────────────────────────

/** SCRUM-365 (D5): offering prices are INR-only — one always-active INR card, no add/remove. */
const INR: Currency = 'INR';

// ─── Validation ───────────────────────────────────────────────────────────────

interface PlanErrors {
  regularPrice?: string;
  earlyBirdPrice?: string;
  trialSessionPrice?: string;
}

function validatePlan(
  p: PricingPlanRequest,
  showEarlyBird: boolean,
  showTrial: boolean,
): PlanErrors {
  const errors: PlanErrors = {};
  const reg = p.regularPrice !== '' ? Number(p.regularPrice) : null;
  const eb =
    showEarlyBird && p.earlyBirdPrice !== undefined && p.earlyBirdPrice !== ''
      ? Number(p.earlyBirdPrice)
      : null;
  const trial =
    showTrial && p.trialSessionPrice !== undefined && p.trialSessionPrice !== ''
      ? Number(p.trialSessionPrice)
      : null;

  const tooManyDecimals = (v: number) => {
    const s = v.toString();
    const dot = s.indexOf('.');
    return dot !== -1 && s.length - dot - 1 > 2;
  };

  if (reg !== null) {
    if (reg <= 0) errors.regularPrice = 'Must be greater than 0';
    else if (tooManyDecimals(reg)) errors.regularPrice = 'Max 2 decimal places';
  }
  if (eb !== null) {
    if (eb <= 0) errors.earlyBirdPrice = 'Must be greater than 0';
    else if (tooManyDecimals(eb)) errors.earlyBirdPrice = 'Max 2 decimal places';
    else if (reg !== null && eb >= reg) errors.earlyBirdPrice = 'Must be less than regular price';
  }
  if (trial !== null) {
    if (trial < 0) errors.trialSessionPrice = 'Cannot be negative';
    else if (tooManyDecimals(trial)) errors.trialSessionPrice = 'Max 2 decimal places';
    else if (reg !== null && trial >= reg) errors.trialSessionPrice = 'Must be less than regular price';
  }

  return errors;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  plans: PricingPlanRequest[];
  onChange: (plans: PricingPlanRequest[]) => void;
  disabled?: boolean;
  showEarlyBirdPrice?: boolean;
  showTrialPrice?: boolean;
}

export function PriceListEditor({
  plans,
  onChange,
  disabled,
  showEarlyBirdPrice = false,
  showTrialPrice = false,
}: Props) {
  const symbol = currencySymbol(INR);
  const plan = plans.find((p) => p.currency === INR);
  const errors = plan ? validatePlan(plan, showEarlyBirdPrice, showTrialPrice) : {};
  const hasErrors = Object.keys(errors).length > 0;

  const updateField = (
    field: 'regularPrice' | 'earlyBirdPrice' | 'trialSessionPrice',
    raw: string,
  ) => {
    const value = raw === '' ? '' : Number(raw);
    if (!plan) {
      // The INR card is always shown; the first edit appends its plan.
      onChange([...plans, { currency: INR, regularPrice: '', [field]: value }]);
      return;
    }
    onChange(
      plans.map((p) =>
        p.currency === INR
          ? { ...p, [field]: value }
          : p,
      ),
    );
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3">
        <div
          className={cn(
            'rounded-2xl border-2 p-4 space-y-4 transition-all duration-150',
            hasErrors
              ? 'border-destructive/40 bg-destructive/5'
              : 'border-orange-200 bg-orange-50',
          )}
        >
          {/* Card header */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
              INR · Indian Rupee
            </span>
          </div>

          {/* Regular price — prominent */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-500">
              Regular price <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base font-semibold text-slate-400 pointer-events-none">
                {symbol}
              </span>
              <Input
                type="number"
                min={0.01}
                step="0.01"
                aria-label="Regular price"
                value={plan?.regularPrice ?? ''}
                onChange={(e) => updateField('regularPrice', e.target.value)}
                disabled={disabled}
                placeholder="0.00"
                className={cn(
                  'pl-8 h-11 text-base font-semibold bg-white',
                  errors.regularPrice ? 'border-destructive' : '',
                )}
              />
            </div>
            {errors.regularPrice && (
              <p className="text-xs text-destructive">{errors.regularPrice}</p>
            )}
          </div>

          {/* Optional price fields */}
          {(showEarlyBirdPrice || showTrialPrice) && (
            <div className="space-y-3 pt-1 border-t border-white/60">
              {showEarlyBirdPrice && (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-400 flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Early bird price
                    <span className="text-slate-300">(optional)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
                      {symbol}
                    </span>
                    <Input
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={plan?.earlyBirdPrice ?? ''}
                      onChange={(e) => updateField('earlyBirdPrice', e.target.value)}
                      disabled={disabled}
                      placeholder="0.00"
                      className={cn(
                        'pl-8 h-9 text-sm bg-white',
                        errors.earlyBirdPrice ? 'border-destructive' : '',
                      )}
                    />
                  </div>
                  {errors.earlyBirdPrice && (
                    <p className="text-xs text-destructive">{errors.earlyBirdPrice}</p>
                  )}
                </div>
              )}

              {showTrialPrice && (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Trial session price
                    <span className="text-slate-300">(optional)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
                      {symbol}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={plan?.trialSessionPrice ?? ''}
                      onChange={(e) => updateField('trialSessionPrice', e.target.value)}
                      disabled={disabled}
                      placeholder="0.00"
                      className={cn(
                        'pl-8 h-9 text-sm bg-white',
                        errors.trialSessionPrice ? 'border-destructive' : '',
                      )}
                    />
                  </div>
                  {errors.trialSessionPrice && (
                    <p className="text-xs text-destructive">{errors.trialSessionPrice}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground pt-1">
        Prices are in Indian Rupees (₹). A regular price is required to publish.
      </p>
    </div>
  );
}
